import { Box, FormHelperText, IconButton, TextField, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ClearIcon } from "@mui/x-date-pickers";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import styles from "./ScanVerificationResultDataTable.styles";
import SecureSyncTable from "src/components/common/SecureSyncTable/SecureSyncTable";
import SecureSyncTableCard from "src/components/common/SecureSyncTableCard/SecureSyncTableCard";
import { resolved, verifyIssueTypeId } from "src/constants/processMediaConstants";
import Pencil from "src/components/Icons/Pencil/Pencil";
import { convertToLocaleUpperCase } from "src/utils/commonHelper";
import SecureSyncCheckBox from "src/components/common/SecureSyncCheckBox/SecureSyncCheckBox";
import { noFlag, yesFlag } from "src/constants/serviceConstants";

const ScanVerificationResultDataTable = forwardRef(({ dataArray = [], getRowId, onDelete }, ref) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [dataList, setDataList] = useState([]);
    const [updatedRows, setUpdatedRows] = useState(new Set());
    const [pagination, setPagination] = useState(0);
    const errorRows = useRef({});

    const attributeChange = (att) => {
        const { pageNumber } = att;
        setPagination(pageNumber);
    };

    useImperativeHandle(ref, () => ({
        getData: () => {
            if (Object.keys(errorRows.current).length > 0) {
                return { error: true, dataList: [] };
            }
            setUpdatedRows(new Set());
            return { error: false, dataList };
        },
    }));

    useEffect(() => {
        setDataList(dataArray);
        checkScannedHasValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataArray]);

    const checkScannedHasValues = () => {
        const newUpdatedRows = new Set(updatedRows);
        for (const item of dataArray) {
            if (!item.c7 && item.c4 === resolved.resolvedYes) {
                newUpdatedRows.add(item.c3);
            }
        }
        setUpdatedRows(newUpdatedRows);
    };

    const checkRawUpdated = (mediaNo, resolvedStatus, comment) => {
        const newUpdatedRows = new Set(updatedRows);
        let newErrorRows = { ...errorRows.current };
        if (resolvedStatus === noFlag && !comment) {
            newUpdatedRows.delete(mediaNo);
        } else {
            newUpdatedRows.add(mediaNo);
        }

        if ((resolvedStatus === noFlag && comment) || (resolvedStatus === yesFlag && !comment)) {
            let errorObj = {};
            if (resolvedStatus === noFlag) {
                errorObj = { ...errorObj, checkBox: true };
            }
            if (!comment) {
                errorObj = { ...errorObj, comment: true };
            }
            newErrorRows = {
                ...newErrorRows,
                [mediaNo]: errorObj,
            };
        } else {
            delete newErrorRows[mediaNo];
        }
        errorRows.current = newErrorRows;
        setUpdatedRows(newUpdatedRows);
    };

    const columns = [
        {
            field: "action",
            headerName: "",
            sortable: true,
            renderCell: (params) => {
                const disableEdit = params.c7 && params.c4 === resolved.resolvedYes;
                const viewDelete = !params.c0 && params.c1 === verifyIssueTypeId.receiveOpenMediaFromIronMountain;
                return (
                    <Box display={"flex"} gap={2} alignItems={"center"}>
                        <IconButton sx={styles.updateIcon} disabled={Boolean(disableEdit)} disableRipple>
                            {updatedRows.has(params.c3) ? (
                                <Pencil fill={theme.palette.im_orange["500"]} />
                            ) : disableEdit ? (
                                <DoneOutlinedIcon sx={styles.disabledIcon} />
                            ) : (
                                <ArticleOutlinedIcon sx={styles.doneIcon} />
                            )}
                        </IconButton>
                        {viewDelete && (
                            <IconButton
                                onClick={() => {
                                    onDelete(params.c3);
                                    const newUpdatedRows = new Set(updatedRows);
                                    newUpdatedRows.delete(params.c3);
                                    setUpdatedRows(newUpdatedRows);
                                }}
                            >
                                <ClearIcon sx={styles.clearIcon} />
                            </IconButton>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "c2",
            headerName: convertToLocaleUpperCase(t("scanVerification.issue")),
            sortable: true,
        },
        {
            field: "c3",
            headerName: convertToLocaleUpperCase(t("scanVerification.mediaNumber")),
            sortable: true,
        },
        {
            field: "c4",
            headerName: convertToLocaleUpperCase(t("scanVerification.resolved")),
            sortable: true,
            renderCell: (param) => {
                const disableEdit = param.c7 && param.c4 === resolved.resolvedYes;
                return (
                    <>
                        <Box sx={styles.centerBoxContent}>
                            <SecureSyncCheckBox
                                key={param.c3}
                                name="Resolved"
                                checked={param?.c4?.toUpperCase() === yesFlag ? true : false}
                                disabled={disableEdit}
                                onChange={(e) => {
                                    const selectedStatus = e.target.checked ? yesFlag : noFlag;
                                    setDataList((prevItems) =>
                                        prevItems.map((item) =>
                                            param.c3 === item.c3 ? { ...item, c4: selectedStatus } : item
                                        )
                                    );
                                    checkRawUpdated(param.c3, selectedStatus, param.c5);
                                }}
                            />
                        </Box>
                        {`${param.c3}` in errorRows.current && errorRows.current[param.c3]?.checkBox && (
                            <FormHelperText error>{t("scanVerification.hasCommentAndNotResolved")}</FormHelperText>
                        )}
                    </>
                );
            },
        },
        {
            field: "c5",
            headerName: `${convertToLocaleUpperCase(t("scanVerification.comments"))} *`,
            width: "230px",
            sortable: true,
            renderCell: (param) => {
                const disableEdit = param.c7 && param.c4 === resolved.resolvedYes;
                return (
                    <>
                        <TextField
                            key={param.c3}
                            aria-label="comments"
                            multiline
                            rows={2}
                            fullWidth
                            value={param?.c5}
                            InputProps={{ readOnly: disableEdit }}
                            onChange={(e) => {
                                const comment = e.target.value;
                                const selectedStatus = e.target.value ? yesFlag : noFlag;
                                setDataList((prevItems) =>
                                    prevItems.map((item) =>
                                        param.c3 === item.c3 ? { ...item, c4: selectedStatus, c5: comment } : item
                                    )
                                );
                                checkRawUpdated(param.c3, selectedStatus, comment);
                            }}
                            helperText={
                                `${param.c3}` in errorRows.current && errorRows.current[param.c3]?.comment
                                    ? t("scanVerification.hasResolvedAndNotComment")
                                    : ""
                            }
                            error={`${param.c3}` in errorRows.current && errorRows.current[param.c3]?.comment}
                        />
                    </>
                );
            },
        },
        {
            field: "c6",
            headerName: convertToLocaleUpperCase(t("scanVerification.originalUserDateTime")),
            sortable: true,
        },
        {
            field: "c7",
            headerName: convertToLocaleUpperCase(t("scanVerification.resolvedUserDateTime")),
            sortable: true,
        },
    ];

    return (
        <SecureSyncTableCard sx={styles.noMarginTop}>
            <Box sx={styles.recordCount}>
                {dataArray.length > 0 && (
                    <>
                        <Box sx={styles.recordStart}>
                            <Typography variant="body1">
                                {t("messages.recordsFound", { count: dataArray.length })}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
            <Box sx={styles.cardContent}>
                <SecureSyncTable
                    rows={dataList}
                    columns={columns}
                    getRowId={getRowId}
                    onTableAttributesChange={attributeChange}
                    initialPage={pagination}
                    noDataMessage={t("scanVerification.noRecordsFound")}
                />
            </Box>
        </SecureSyncTableCard>
    );
});

ScanVerificationResultDataTable.displayName = "ScanVerificationResultDataTable";
export default ScanVerificationResultDataTable;
